/**
 * Lableforge data model — THE CONTRACT.
 *
 * Every other module (db, gemini client, audio slicer, pages) builds on these
 * shapes. Feature work must NOT change the fields below; add new modules or
 * optional helper types instead.
 *
 * Lifecycle recap:
 *   1. User uploads/records audio            -> Task { status: 'new' }
 *   2. First pass to Gemini                  -> Task { status: 'processing' }
 *   3. Segments stored + clips sliced by app -> Task { status: 'review' }
 *   4. Every segment resolved                -> Task { status: 'completed' }
 */

/* ------------------------------------------------------------------ */
/* Task                                                                */
/* ------------------------------------------------------------------ */

/**
 * Where a task sits in the pipeline.
 * - `new`        : audio stored, first pass not started
 * - `processing` : Gemini call and/or audio slicing in flight
 * - `review`     : segments + clips exist, awaiting verify/correct
 * - `completed`  : every segment is `correct` or has been corrected
 */
export type TaskStatus = 'new' | 'processing' | 'review' | 'completed';

/** One uploaded/recorded audio file and its labelling job. */
export interface Task {
  /** Stable unique id (uuid). Primary key in the `tasks` store. */
  id: string;
  /** Human-facing name — usually the original filename. */
  name: string;
  /** Epoch milliseconds, used for sorting newest-first. */
  createdAt: number;
  /** Pipeline stage. See {@link TaskStatus}. */
  status: TaskStatus;
  /** Total length of the original audio in seconds (decoded, not estimated). */
  durationSec: number;
  /** Human-readable failure reason when a first pass / slice blew up. */
  error?: string;
  /** ISO 639-3 language detected by the first pass (drives later re-splits). */
  language?: string;
  /**
   * Short display code for the recording, e.g. "A1". Clip names derive from
   * it: A1.1, A1.2, … — the human-readable face of the lineage tree.
   */
  code?: string;
  /**
   * The plan id that produced this task's clips, e.g.
   * "whisperx+gemini+recut-guided+fillers-force". Decoded for display by
   * services/enginePlan.ts — the full trace of what ran.
   */
  engine?: string;
}

/**
 * One word INSIDE a clip, with its own identity. Words are entities: when a
 * word moves to a neighbouring clip it keeps its id — the record of "this
 * exact word travelled from A1.1 to A1.2" is what the audit trail replays.
 */
export interface Word {
  id: string;
  text: string;
}

/* ------------------------------------------------------------------ */
/* Segment                                                             */
/* ------------------------------------------------------------------ */

/**
 * What kind of audible thing a segment is.
 * Gemini must cover EVERYTHING audible, not only clean words.
 * - `word`    : an actual spoken word
 * - `filler`  : umm / hmm / uh / er and friends
 * - `noise`   : background/non-speech sound (door, cough, music, click)
 * - `unclear` : speech-like but unintelligible
 */
export type SegmentKind = 'word' | 'filler' | 'noise' | 'unclear' | 'silence';

/** Human verification state of a segment. */
export type VerifyState = 'pending' | 'correct' | 'incorrect';

/**
 * A machine's opinion that a segment should not exist at all.
 *
 * WHY THIS IS A FLAG AND NOT A DELETE
 * -----------------------------------
 * Gap fill inserts words and never deletes any (see services/gapFill.ts), so a
 * word the first pass mis-placed and gap fill then re-inserted at its real
 * position exists TWICE — the stale copy sitting on audio it does not match.
 * Left as `pending` it looks like innocent work to verify; the human has no way
 * to tell it apart from a real word until they play it.
 *
 * So the duplicate resolver marks it `verify: 'incorrect'` with no correction,
 * which puts it in the Correct queue, and attaches this: the reason, and the
 * copy it lost against. The Correct card renders it as a one-tap suggested
 * removal. Nothing here decides anything on its own — a human still taps.
 *
 * PRODUCED ONLY BY THE LEGACY FALLBACK. The sole writer is
 * `services/legacy/duplicateResolve.ts`, which runs only on the legacy branch
 * of the first pass (phones, and any machine with no helper server). Tasks from
 * the aligned pipeline never carry this.
 *
 * Of the four fields below only `hint` has a reader. The field set is
 * aspirational, not load-bearing.
 */
export interface RemovalSuggestion {
  /** What the machine thinks this row is. One value today; a union tomorrow. */
  reason: 'duplicate';
  /** One line, human-facing, rendered on the Correct card. THE ONLY FIELD READ. */
  hint: string;
  /** WRITE-ONLY today. The segment judged to be the plausible copy. */
  counterpartId?: string;
  /** WRITE-ONLY today. Epoch ms, so a stale suggestion can be told from a fresh one. */
  at: number;
}

/**
 * One time-sliced piece of a task's audio, as proposed by the first pass and
 * then judged by a human.
 *
 * `heard` vs `contextual` is the whole point of the app:
 *   heard      = verbatim phonetics, NOT corrected by context ("gonna", "libary")
 *   contextual = what the word most likely is in context   ("going to", "library")
 *
 * READ THIS BEFORE ADDING A FIELD
 * -------------------------------
 * Roughly half of the interface below is UNPOPULATED in the most common
 * deployment. Phones cannot run the helper servers, so they always take the
 * legacy fallback path, and that path writes none of the `words` / `name` /
 * `trace` / `alternatives` / `madeFrom` group. The four headings say which
 * path writes what — the field list is NOT a uniform contract.
 *
 * NO FIELD IS EVER REMOVED FROM THIS INTERFACE. There is no migration and no
 * server copy: stored rows written by every past design must keep parsing, so
 * readers must tolerate every field on every row.
 */
export interface Segment {
  /* ---- core: written by every path ---- */
  /** Stable unique id (uuid). Primary key in the `segments` store. */
  id: string;
  /** Owning {@link Task.id}. Indexed. */
  taskId: string;
  /**
   * 0-based DISPLAY ORDINAL within the task — the "#n of m" on a card.
   *
   * NOT the ordering key: {@link startSec} is. The two agree out of a first
   * pass and can disagree afterwards, because `db.removeSegment` renumbers over
   * the UNFILTERED sibling set while a recut renumbers leaves only. Sort by
   * `startSec` when you mean time; use `index` only to show a human a number.
   */
  index: number;
  /**
   * Clip start offset in seconds from the beginning of the original audio.
   * THE ORDERING KEY, and the coordinate the partition invariant is stated in.
   */
  startSec: number;
  /** Clip end offset in seconds. Always > startSec. */
  endSec: number;
  /** Verbatim phonetic transcription of what was ACTUALLY heard. */
  heard: string;
  /** What the word likely is given the surrounding context. */
  contextual: string;
  /** Model trust score, 0..1. */
  confidence: number;
  /** See {@link SegmentKind}. */
  kind: SegmentKind;
  /** Human verdict from the Verify tab. */
  verify: VerifyState;
  /** Human-typed replacement text, set in the Correct tab. */
  correctedText?: string;
  /** Human-chosen replacement kind, set in the Correct tab. */
  correctedKind?: SegmentKind;
  /**
   * Set to 'human' when a person positioned these boundaries (trimmer save,
   * drag-to-align, confirmed merge). This — not `confidence === 1`, which the
   * model can also emit — is what marks a row as ground truth the pipeline and
   * the ripple must never move.
   */
  placedBy?: 'human';
  /**
   * Set (ms epoch) when a recut replaced this record. THE CURRENT SUPERSEDE
   * MECHANISM — written only by `recut.applyRecut`, read by every leaf filter
   * via {@link isActiveLeaf}.
   *
   * Obsolete records keep their stamps and words forever (restorable,
   * auditable) but their cached clip audio is dropped — all audio lives once,
   * in the task's original file; clips are just timestamps into it.
   */
  obsoleteAt?: number;

  /* ---- aligned pipeline only — absent on every legacy-path row ---- */
  /**
   * The words inside this clip, in spoken order, each with its own id.
   *
   * Written by `pipeline/alignedTiling` and `recut.applyRecut`. For rows
   * WITHOUT it, `recut.wordsOf` mints fresh ids on every call, so the "a word
   * keeps its id" promise holds only within a single service call, and only
   * for aligned-path rows.
   */
  words?: Word[];
  /**
   * Human-readable version name, e.g. "A1.4": the task's code plus a
   * per-recording sequence number that only ever counts up. Names are never
   * reused — a recut always mints new ones.
   *
   * This is `model/ids.ts`'s `Node.path`. Re-parsed by regex in
   * `recut.nextNames` to find the next free number.
   */
  name?: string;
  /**
   * The names/ids of the record(s) this one was made from. THE lineage field:
   * "A1.4 made from [A1.1, A1.2]". The dotted name is just a label; this
   * list is the actual tree.
   *
   * WRITE-ONLY today — nothing reads it. Kept because it is the precursor of
   * `model/ids.ts`'s `Proposal.derivedFrom`. MIGRATION OBSTACLE: it stores a
   * MIX of `name` and `id` (`recut.ts` writes `s.name ?? s.id`), so it cannot
   * be joined without knowing which one a given entry got. A future recut
   * should write ids only.
   */
  madeFrom?: string[];
  /**
   * Gemini's top guesses for this clip, computed during PROCESSING so the
   * Verify screen's correction modal opens instantly instead of waiting on a
   * request while a person is mid-review.
   *
   * Legacy-path tasks reach Verify with this UNDEFINED, and the picker falls
   * back to type-it-yourself.
   */
  alternatives?: Array<{ text: string; confidence: number }>;
  /**
   * Which pipeline stages produced this clip, in order — e.g.
   * ["whisperx", "gemini:read-cut", "recut:guided"]. Written at creation by
   * every stage that makes or rewrites a clip, so a suspicious label can be
   * traced to the exact step that invented it.
   *
   * WRITE-ONLY / DISPLAY-ONLY audit data. No programmatic consumer.
   */
  trace?: string[];

  /* ---- legacy repair tower only ---- */
  /**
   * The pipeline believes this row should be removed. See
   * {@link RemovalSuggestion}. Purely advisory: it changes what the Correct card
   * OFFERS, never what a verdict means. Cleared the moment a human disagrees.
   *
   * Written ONLY by `services/legacy/duplicateResolve.ts`. It does have a live
   * UI consumer (the ⚠ hint on the Correct card), so it is not internal to the
   * pipeline — do not remove it in a dead-code sweep.
   */
  suggestedRemoval?: RemovalSuggestion;

  /* ---- superseded or half-wired designs.
         Rows in existing databases still carry these. ---- */
  /**
   * Stage-1 sign-off (ms epoch): a human listened and approved WHERE this clip
   * is cut.
   *
   * LIVE, but narrower than it looks. It drives the Cuts queue and its badge
   * ONLY — Verify no longer gates on it (`db.getPendingVerifySegments` does not
   * test it), so the Cuts and Verify queues deliberately OVERLAP and the two
   * tab badges are NOT disjoint counts.
   *
   * The rule "any boundary change clears it" is UNENFORCED: the only clearer,
   * `db.revokeCutApproval`, has no caller, while `moveBoundary`, `setBoundaries`
   * and `mergeAdjacentSegments` all carry the stamp through a span change.
   */
  cutApprovedAt?: number;
  /**
   * HISTORICAL — the split-container scheme. A split row was a CONTAINER: its
   * children tiled its span and it left every queue and count.
   *
   * Its only writer now lives in `services/legacy/splitSegment.ts` and has no
   * entry point, so NO NEW ROW CAN ACQUIRE IT. The ~15 `!splitAt` read filters
   * stay because stored rows may still carry it; when no stored row does, the
   * filters and this field can go together.
   */
  splitAt?: number;
  /**
   * DEPRECATED — do not add readers.
   *
   * HISTORICAL: written by the removed split flow; NOTHING HAS EVER READ IT.
   * Name collision to resolve before wiring `model/ids.ts`: `Node.parentId`
   * there means something different (tree parent of the accepted view). The
   * name is free precisely because this one is dead.
   */
  parentId?: string;
}

/* ------------------------------------------------------------------ */
/* Derived / transport shapes                                          */
/* ------------------------------------------------------------------ */

/**
 * Exactly the object Gemini is asked to return per segment (structured JSON).
 * No ids, no verify state — the app assigns those when persisting.
 */
export interface RawSegment {
  startSec: number;
  endSec: number;
  heard: string;
  contextual: string;
  confidence: number;
  kind: SegmentKind;
}

/** Progress counters for a task, used by the tab badges and progress bars. */
export interface TaskProgress {
  total: number;
  pending: number;
  correct: number;
  incorrect: number;
  /** Segments that are `correct`, or `incorrect` but already given a correction. */
  resolved: number;
}

/** Shape written by the "Export JSON" button on the Done tab. */
export interface ExportPayload {
  task: Task;
  exportedAt: number;
  segments: Array<
    Segment & {
      /** Final label: `correctedText` when present, otherwise `heard`. */
      finalText: string;
      /** Final kind: `correctedKind` when present, otherwise `kind`. */
      finalKind: SegmentKind;
    }
  >;
}

/* ------------------------------------------------------------------ */
/* Small shared helpers over the contract                              */
/* ------------------------------------------------------------------ */

/**
 * Is this row a LIVE LEAF — one of the rows that currently tile the audio?
 *
 * THE PARTITION INVARIANT is stated over exactly this set: the active leaves of
 * a task tile its audio exactly, every millisecond labelled once. Two different
 * superseding mechanisms can take a row out of it:
 *
 *   `splitAt`    HISTORICAL. The row was decomposed into children that tile its
 *                span. No live writer remains (see `services/legacy/`), but
 *                stored databases still contain rows carrying it.
 *   `obsoleteAt` CURRENT. A recut replaced the row. Written by
 *                `recut.applyRecut`.
 *
 * This predicate was open-coded as `!s.splitAt && !s.obsoleteAt` at TWENTY
 * sites across eight files with no named definition anywhere — which is exactly
 * why three sites were missing it and one was incomplete. Read filters must use
 * this; queues, counts, progress and export all mean this set.
 */
export function isActiveLeaf(s: Segment): boolean {
  return !s.splitAt && !s.obsoleteAt;
}

/**
 * The live leaves of a row set, in TIME order (`startSec`).
 *
 * Ordered by `startSec` rather than `index` deliberately: `index` is a display
 * ordinal that can disagree with time order after a remove-then-recut (see
 * {@link Segment.index}), whereas `startSec` is the ordering key the partition
 * is defined on.
 */
export function activeLeaves(rows: Segment[]): Segment[] {
  return rows.filter(isActiveLeaf).sort((a, b) => a.startSec - b.startSec);
}

/** A segment counts as resolved when it needs no more human attention. */
export function isSegmentResolved(s: Segment): boolean {
  if (s.verify === 'correct') return true;
  if (s.verify === 'incorrect') return Boolean(s.correctedText || s.correctedKind);
  return false;
}

/** Final label text for a segment (correction wins over the model's guess). */
export function finalTextOf(s: Segment): string {
  return s.correctedText ?? s.heard;
}

/** Final kind for a segment (correction wins over the model's guess). */
export function finalKindOf(s: Segment): SegmentKind {
  return s.correctedKind ?? s.kind;
}
